import { TextBox } from '../../../typings/ui';
import { DeviceDetection } from '../common';
import { DWCore, IUiComponentFactory, Utils } from '../dynamicWorkspace';
import { ITemplateExtension } from '../extensions/interfaces/iTemplateExtension';
import { ILanguageProvider } from '../language';
import { AboutRouter, RootRouter, RouteManager, SettingsRouter, ViewRouter } from '../router';
import { SystemSettingsRouter } from '../router/systemSettingsRouter';
import { IStorage } from '../storage';
import { IThemeManager } from '../themeManager';

/**
 * This class will handle the navigation panel within DW.
 *
 * @export
 * @class NavigationPanelHandler
 */
export class NavigationPanelHandler {
    private window: Window;
    private navigationPanelRootElement: HTMLElement;
    private systemSettingsLinkElement: HTMLLinkElement | null = null;
    private languageProvider: ILanguageProvider;

    private _isCollapsed = false;
    public get isCollapsed() {
        return this._isCollapsed;
    }

    private _isAdmin = false;
    public set isAdmin(value: boolean) {
        this._isAdmin = value;
        if (this.systemSettingsLinkElement) {
            this.systemSettingsLinkElement.hidden = !this._isAdmin || Utils.Instance.deviceDetection.isMobile();
        }
    }

    private readonly autoCollapseTreshold = 500;
    private shrinkButtonElement: HTMLButtonElement | null = null;
    private navigationHeaderPanel: HTMLElement | null = null;
    private applicatioContainerElement: HTMLElement | null = null;
    private viewNavigationElement: HTMLElement | null = null;
    private viewSearchTextBox?: TextBox;
    private viewRouter: ViewRouter;
    private rootRouter: RootRouter;
    private aboutRouter: AboutRouter;
    private settingsRouter: SettingsRouter;
    private systemSettingsRouter: SystemSettingsRouter;
    private localStorage: IStorage;
    private themeManager: IThemeManager;
    private templateExtension: ITemplateExtension;
    private uiComponentFactory: IUiComponentFactory;
    private isUpdating = false;
    private isNavigationCollapsedByUser = false;

    /**
     * Executes if a search for views should occur.
     *
     * @memberof NavigationPanelHandler
     */
    public onSearchViews?: (searchTerm: string) => void;


    /**
     * Creates an instance of NavigationPanelHandler.
     * 
     * @param {Window} window The browser window instance.
     * @param {HTMLElement} navigationRootElement The navigation root.
     * @param {ILanguageProvider} languageProvider The language provider.
     * @param {ViewRouter} viewRouter The view router.
     * @param {RootRouter} rootRouter The root router.
     * @param {AboutRouter} aboutRouter The about router.
     * @param {SettingsRouter} settingsRouter The settings router.
     * @param {SystemSettingsRouter} systemsSettingsRouter The system settings router.
     * @param {IThemeManager} themeManager The theme manager.
     * @param {ITemplateExtension} templateExtension The template extension.
     * @param {IStorage} localStorage Localstorage 
     * @param {IUiComponentFactory} uiComponentFactory The ui component factory. 
     * @memberof NavigationPanelHandler
     */
    public constructor(window: Window, navigationRootElement: HTMLElement, languageProvider: ILanguageProvider,
        viewRouter: ViewRouter, rootRouter: RootRouter, aboutRouter: AboutRouter, settingsRouter: SettingsRouter,
        systemsSettingsRouter: SystemSettingsRouter, themeManager: IThemeManager, templateExtension: ITemplateExtension, localStorage: IStorage, uiComponentFactory: IUiComponentFactory) {

        this.window = window;
        this.navigationPanelRootElement = navigationRootElement;
        this.languageProvider = languageProvider;
        this.viewRouter = viewRouter;
        this.rootRouter = rootRouter;
        this.aboutRouter = aboutRouter;
        this.settingsRouter = settingsRouter;
        this.localStorage = localStorage;
        this.themeManager = themeManager;
        this.templateExtension = templateExtension;
        this.uiComponentFactory = uiComponentFactory;
        this.systemSettingsRouter = systemsSettingsRouter;
        this.initNavigationPanel();
        if (!Utils.Instance.deviceDetection.isMobile()) {
            this.handleInitialNavigationState();
        }
    }

    /**
     * Init the navigation with language, a11y, events etc.
     *
     * @private
     * @memberof NavigationPanelHandler
     */
    private initNavigationPanel(): void {

        this.applicatioContainerElement = this.window.document.querySelector('.wd-application-container');

        // Navigation header panel
        this.navigationHeaderPanel = this.navigationPanelRootElement.querySelector<HTMLElement>('.wd-navigation-header-panel');
        if (this.navigationHeaderPanel) {
            const viewModel = {
                goHomeText: this.languageProvider.get('framework.header.goHome'),
                customConfig: this.themeManager.getTheme()
            };
            this.templateExtension.render(this.navigationHeaderPanel, require('./templates/navigationHeaderPanel.html'), viewModel, true);
        }

        this.viewNavigationElement = this.navigationPanelRootElement.querySelector<HTMLElement>('#wd-view-navigation');

        const viewHeadingElement = this.navigationPanelRootElement.querySelector<HTMLHeadingElement>('.wd-view-navigation-view-heading');
        if (viewHeadingElement) {
            viewHeadingElement.innerText = this.languageProvider.get('framework.navigation.headings.views');
        }
        const optionsHeadingElement = this.navigationPanelRootElement.querySelector<HTMLHeadingElement>('.wd-view-navigation-options-heading');
        if (optionsHeadingElement) {
            optionsHeadingElement.innerText = this.languageProvider.get('framework.navigation.headings.options');
        }

        const isOnMobile = Utils.Instance.deviceDetection.isMobile();
        const viewSearchArea = this.navigationPanelRootElement.querySelector<HTMLElement>('.wd-navigation-panel-button-group');
        if (viewSearchArea) {
            viewSearchArea.hidden = isOnMobile;
        }
        if (isOnMobile) {
            const homeLink = this.navigationPanelRootElement.querySelector<HTMLElement>('a');
            if (homeLink) {
                // Don't redirect to start page for mobile.
                homeLink.addEventListener('click', (event) => {
                    event.preventDefault();
                });
            }
        }

        this.shrinkButtonElement = this.navigationPanelRootElement.querySelector<HTMLButtonElement>('.wd-shrink-navigation-button');
        if (this.shrinkButtonElement) {
            this.shrinkButtonElement.title = this.languageProvider.get('framework.navigation.collapseButton.collapse');
            this.shrinkButtonElement.addEventListener('click', () => {
                if (this.navigationPanelRootElement.classList.contains('collapsed')) {
                    this.localStorage.setItem('DynamicWorkspace-NavigationState', 'extended');
                    this.extend();
                } else {
                    this.localStorage.setItem('DynamicWorkspace-NavigationState', 'collapsed');
                    this.isNavigationCollapsedByUser = true;
                    this.collapse();
                }
            });
        }
        const viewSearchInputElement = this.navigationPanelRootElement.querySelector<HTMLInputElement>('.wd-view-search-input');
        if (viewSearchInputElement) {
            this.viewSearchTextBox = this.uiComponentFactory.textBox(viewSearchInputElement);
            this.viewSearchTextBox.setOptions({
                placeholderText: this.languageProvider.get('framework.navigation.search.input.placeHolder'),
                type: 'search'
            });
            this.viewSearchTextBox.bootstrap();
            this.viewSearchTextBox.onValueChanged = ((value) => {
                if (this.onSearchViews && typeof value === 'string') {
                    this.onSearchViews(value);
                }
            });
        }

        const settingsLinkElement = this.navigationPanelRootElement.querySelector<HTMLLinkElement>('#wd-option-item-settings');
        if (settingsLinkElement) {
            this.settingsRouter.registerOnSettingsNavigation(() => {

                // Reset special theme styles
                this.themeManager.useCurrentThemeDefaultValues();

                this.disableSettingsActiveIndicator();
                settingsLinkElement.classList.add('active');
            });
            settingsLinkElement.title = this.languageProvider.get('framework.navigation.settingsButton.title');
            settingsLinkElement.addEventListener('click', () => {
                RouteManager.navigate('/settings');
            });
            const settingsLinkText = settingsLinkElement.querySelector<HTMLButtonElement>('.wd-navigation-option-name');
            if (settingsLinkText) {
                settingsLinkText.textContent = this.languageProvider.get('framework.navigation.settingsButton.title');
            }
        }

        const aboutLinkElement = this.navigationPanelRootElement.querySelector<HTMLLinkElement>('#wd-option-item-about');
        if (aboutLinkElement) {
            this.aboutRouter.registerOnAboutNavigation(() => {

                // Reset special theme styles
                this.themeManager.useCurrentThemeDefaultValues();

                this.disableSettingsActiveIndicator();
                aboutLinkElement.classList.add('active');
            });
            aboutLinkElement.title = this.languageProvider.get('framework.navigation.aboutButton.title');
            aboutLinkElement.addEventListener('click', () => {
                RouteManager.navigate('/about');
            });
            const aboutLinkText = aboutLinkElement.querySelector<HTMLButtonElement>('.wd-navigation-option-name');
            if (aboutLinkText) {
                aboutLinkText.textContent = this.languageProvider.get('framework.navigation.aboutButton.title');
            }
        }

        this.systemSettingsLinkElement = this.navigationPanelRootElement.querySelector<HTMLLinkElement>('#wd-option-item-systemsettings');
        if (this.systemSettingsLinkElement) {
            this.systemSettingsRouter.registerOnSystemSettingsNavigation(() => {

                // Reset special theme styles
                this.themeManager.useCurrentThemeDefaultValues();

                this.disableSettingsActiveIndicator();
                if (this.systemSettingsLinkElement) {
                    this.systemSettingsLinkElement.classList.add('active');
                }
            });
            this.systemSettingsLinkElement.title = this.languageProvider.get('framework.navigation.systemSettingsButton.title');
            this.systemSettingsLinkElement.addEventListener('click', () => {
                RouteManager.navigate('/systemsettings');
            });
            const systemSettingsLinkText = this.systemSettingsLinkElement.querySelector<HTMLButtonElement>('.wd-navigation-option-name');
            if (systemSettingsLinkText) {
                systemSettingsLinkText.textContent = this.languageProvider.get('framework.navigation.systemSettingsButton.title');
            }
        }
        this.rootRouter.registerOnRootNavigation(() => {
            if (aboutLinkElement) {
                aboutLinkElement.classList.remove('active');
            }
            if (settingsLinkElement) {
                settingsLinkElement.classList.remove('active');
            }
            if (this.systemSettingsLinkElement) {
                this.systemSettingsLinkElement.classList.remove('active');
            }
        });
        this.viewRouter.registerOnViewChange(() => {
            if (aboutLinkElement) {
                aboutLinkElement.classList.remove('active');
            }
            if (settingsLinkElement) {
                settingsLinkElement.classList.remove('active');
            }
            if (this.systemSettingsLinkElement) {
                this.systemSettingsLinkElement.classList.remove('active');
            }
            if (this.viewSearchTextBox) {
                this.viewSearchTextBox.setValue('');
            }
        });

        if (this.window) {
            if (!isOnMobile) {
                const resizeDebounceDelay = 50;
                let timeout: any;
                this.window.addEventListener('resize', () => {
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                        this.handleAutoCollapse();
                    }, resizeDebounceDelay);
                });
            } else {
                // Swipe gesture not refined yet pretty simple
                let touchstartX = 0;
                let touchendX = 0;
                let timeTouchStart: number;
                let timeTouchEnd: number;

                // The time in milliseconds the movement should take, to be considered as a swipe
                const swipeTimeThreshold = 700;

                // The distance in pixels the movement should take, to be considered as a swipe
                const phoneSwipeDistanceThreshold = 200;
                const tabletSwipeDistanceThreshold = 150;
                const swipeDistanceThreshold = Utils.Instance.deviceDetection.isPhone() ? phoneSwipeDistanceThreshold : tabletSwipeDistanceThreshold;

                // The width of the area from the left edge, in which the swipe gesture is activated
                const edgeSwipeActivationZone = 20; // 20px from the left edge

                this.window.addEventListener('touchstart', (event) => {
                    touchstartX = event.changedTouches[0].screenX;
                    timeTouchStart = new Date().getTime();
                });
                this.window.addEventListener('touchend', (event) => {
                    touchendX = event.changedTouches[0].screenX;
                    timeTouchEnd = new Date().getTime();

                    // Check if the swipe gesture started in the defined area or if the navigation panel is already open
                    const isLeftEdgeSwipe = touchstartX <= edgeSwipeActivationZone;
                    const isNavigationOpen = this.navigationPanelRootElement.classList.contains('wd-display-navigation-panel-mobile-like');

                    if ((timeTouchEnd - timeTouchStart) < swipeTimeThreshold) {
                        if (Math.abs(touchstartX - touchendX) > swipeDistanceThreshold) {
                            // Should be a swipe gesture
                            if (touchendX > touchstartX && isLeftEdgeSwipe) {
                                // Right swipe from the left edge
                                this.navigationPanelRootElement.classList.add('wd-display-navigation-panel-mobile-like');
                            } else if (touchendX < touchstartX && isNavigationOpen) {
                                // Left swipe, only if the navigation panel is open
                                this.navigationPanelRootElement.classList.remove('wd-display-navigation-panel-mobile-like');
                            }
                        }
                    }
                });
            }
        }
    }

    /**
     * Disables the settings active indicator.
     *
     * @private
     * @memberof NavigationPanelHandler
     */
    private disableSettingsActiveIndicator(): void {
        const settingsLinkElement = this.navigationPanelRootElement.querySelector<HTMLLinkElement>('#wd-option-item-settings');
        const aboutLinkElement = this.navigationPanelRootElement.querySelector<HTMLLinkElement>('#wd-option-item-about');
        if (aboutLinkElement) {
            aboutLinkElement.classList.remove('active');
        }
        if (settingsLinkElement) {
            settingsLinkElement.classList.remove('active');
        }
        if (this.systemSettingsLinkElement) {
            this.systemSettingsLinkElement.classList.remove('active');
        }
    }

    /**
     * Collapse the navigation.
     *
     * @private
     * @memberof NavigationPanelHandler
     */
    private collapse(): void {
        if (this.shrinkButtonElement && !this.isCollapsed) {
            const shrinkButtonElementSpan = this.shrinkButtonElement.querySelector('span');
            this.navigationPanelRootElement.style.width = '';
            this.navigationPanelRootElement.classList.add('collapsed');
            this.shrinkButtonElement.title = this.languageProvider.get('framework.navigation.collapseButton.extend');
            shrinkButtonElementSpan?.classList.remove('angles-left');
            shrinkButtonElementSpan?.classList.add('angles-right');

            if (this.navigationHeaderPanel) {
                this.navigationHeaderPanel.classList.add('collapsed');
            }

            this._isCollapsed = true;
        }

    }

    /**
     * Extend the navigation.
     *
     * @private
     * @memberof NavigationPanelHandler
     */
    private extend(): void {
        if (this.shrinkButtonElement && this.isCollapsed) {
            const shrinkButtonElementSpan = this.shrinkButtonElement.querySelector('span');
            this.shrinkButtonElement.title = this.languageProvider.get('framework.navigation.collapseButton.collapse');
            this.navigationPanelRootElement.classList.remove('collapsed');
            shrinkButtonElementSpan?.classList.add('angles-left');
            shrinkButtonElementSpan?.classList.remove('angles-right');

            if (this.navigationHeaderPanel) {
                this.navigationHeaderPanel.classList.remove('collapsed');
            }
            this.isNavigationCollapsedByUser = false;
            this._isCollapsed = false;
        }
    }

    /**
     * Hides the complete navigation.
     *
     * @memberof NavigationPanelHandler
     */
    public hideNavigation(): void {
        this.navigationPanelRootElement.hidden = true;
    }

    /**
     *Shows the navigation.
     *
     * @memberof NavigationPanelHandler
     */
    public showNavigation(): void {
        this.navigationPanelRootElement.hidden = false;

        const activeViewElement = this.navigationPanelRootElement.querySelector<HTMLElement>('.wd-navigation-panel-item.active');
        if (activeViewElement && this.viewNavigationElement) {
            this.viewNavigationElement.scrollTo(
                {
                    top: activeViewElement.offsetTop
                });
        }
    }

    /**
    * Handel navigation state (collapsed or extended)
    *
    * @memberof NavigationPanelHandler
    */
    public handleInitialNavigationState(): void {
        const currentDevice = DeviceDetection.getCurrentDevice();
        const navState = this.localStorage.getItem('DynamicWorkspace-NavigationState');
        // Default behavior
        if (!navState) {
            switch (currentDevice) {
                case DWCore.Common.Devices.PHONE:
                    this.collapse(); // Collapse on mobile
                    break;
                case DWCore.Common.Devices.TABLET:
                case DWCore.Common.Devices.DESKTOP:
                    this.extend(); // Expand on tablet and desktop
                    break;
            }
        } else {
            if (navState === 'collapsed' && !this.isCollapsed) {
                this.collapse();
            } else if (navState === 'extended' && this.isCollapsed) {
                this.extend();
            }
        }
    }
    /**
     * Handles the auto-collapse feature.
     *
     * @memberof NavigationPanelHandler
     */
    public handleAutoCollapse(): void {
        if (this.applicatioContainerElement && this.navigationHeaderPanel && this.shrinkButtonElement) {
            const appWidth = this.applicatioContainerElement.clientWidth;
            if (appWidth > this.autoCollapseTreshold) {
                this.shrinkButtonElement.disabled = false;
                if (!this.isUpdating && !this.isNavigationCollapsedByUser) {
                    this.isUpdating = true;
                    this.extend();
                    this.isUpdating = false;
                }
            } else {
                this.shrinkButtonElement.disabled = true;
                if (!this.isUpdating && !this.isNavigationCollapsedByUser) {
                    this.isUpdating = true;
                    this.collapse();
                    this.isUpdating = false;
                }
            }
        }
    }
}